"use client";

import React, { useState } from "react";

// The updatable board of verification opportunities. New postings from orgs go
// into OPPORTUNITY_POSTINGS below (org blurbs are approved by the org before
// release; keep edits to what they sent). Filter chips are derived from the
// tags in use, so adding a posting with a new tag extends the filter row.

type OpportunityTag = "jobs" | "funding" | "community" | "collaboration" | "learning";

const TAG_LABELS: Record<OpportunityTag, string> = {
  jobs: "Jobs",
  funding: "Funding",
  community: "Community",
  collaboration: "Collaboration",
  learning: "Learning",
};

const TAG_ORDER: OpportunityTag[] = ["jobs", "funding", "collaboration", "community", "learning"];

const OPPORTUNITY_POSTINGS: {
  org: string;
  tagline: string;
  tags: OpportunityTag[];
  blurb: string;
  links: { text: string; url: string }[];
}[] = [
  {
    org: "Lucid Computing",
    tagline: "The independent verification layer for AI compute",
    tags: ["jobs", "collaboration"],
    blurb:
      "Lucid builds the hardware-rooted verification layer that lets an outside party check what actually ran on an AI cluster (which model, on which chips, under which policy) without having to trust any single party or any single piece of silicon. They collaborate with outside researchers and engineers on TEE hardening, side-channel and telemetry-based workload verification, and cryptographic inference and training verification, and they hire across hardware, cryptography, systems, and policy. They are also happy to point newcomers to technical verification at a first tractable project.",
    links: [
      { text: "lucidcomputing.ai", url: "https://lucidcomputing.ai" },
      { text: "Open roles", url: "https://lucidcomputing.ai/careers" },
    ],
  },
  {
    org: "Amodo Design",
    tagline: "Engineering the inference-only retrofit",
    tags: ["jobs", "collaboration"],
    blurb:
      "Amodo is a hardware engineering firm prototyping the network-tap and recomputation approach at the center of the plan's inference-only proposal, including passive optical taps and an implementation of the DiFR recomputation scheme. Alongside Plan A they published the workstream-by-workstream SITREP featured above and role-specific recommendations for how to help.",
    links: [
      { text: "AI verification at Amodo", url: "https://amododesign.com/ai-verification" },
      { text: "Careers", url: "https://amododesign.com/careers" },
    ],
  },
  {
    org: "FLI + Singapore AI Safety Hub",
    tagline: "Building a Confidential Network Logger prototype",
    tags: ["collaboration", "jobs"],
    blurb:
      "Bridging the political gap between Asia and the West, this collaboration between the Future of Life Institute and the Singapore AI Safety Hub is building a prototype of a Confidential Network Logger, the device that would allow for the inference-only retrofitting contained in Plan A. SASH is hiring for this work, and a get-involved form is on the way.",
    links: [
      { text: "Making AI verification international", url: "https://www.aisafety.sg/blog/making-ai-verification-international" },
    ],
  },
  {
    org: "Compute Verification Project",
    tagline: "Developing new mechanisms",
    tags: ["collaboration", "jobs"],
    blurb:
      "The Compute Verification Project is a research nonprofit designing new protocols for verifying how compute is used without revealing confidential information. We developed the current SOTA inference verification algorithms as well as an inference server with deterministic builds, serving, and networking. Most recently, we published a working draft of a new protocol that can structurally distinguish between inference and training based on communication patterns. We show that it renders standard frontier training algorithms infeasible, and are organizing a competition to develop new training algorithms that circumvent it. We are well-funded and are hiring technical generalists with backgrounds in security, systems, and ML.",
    links: [{ text: "Get in touch", url: "mailto:dreuter14@gmail.com" }],
  },
  {
    org: "RAND Center on AI, Security and Technology (RAND CAST)",
    tagline: "Foundational technical and policy research on AI verification",
    tags: ["jobs"],
    blurb:
      "The RAND AI verification team focuses on research projects that advance technical verification mechanisms for international agreements on AI and other applications: conducting in-depth analysis, writing products for senior stakeholders, and advising external organizations on R&D projects. RAND CAST is currently seeking technical and policy researchers to join its AI verification workstream.",
    links: [
      {
        text: "Expression of interest for working with us",
        url: "https://forms.gle/2kvzhSHaA7SwJmER8",
      },
      {
        text: "RAND CAST",
        url: "https://www.rand.org/global-and-emerging-risks/centers/ai-security-and-technology.html",
      },
    ],
  },
  {
    org: "Oxford University",
    tagline: "Research & policy enabling high-stakes agreements",
    tags: ["jobs", "collaboration"],
    blurb:
      "The Oxford Martin AI Governance Initiative (AIGI) pairs technical analysis of AI systems with deep policy work to anticipate and reduce the risks of advanced AI. Within AIGI, the Hardware AI Governance Lab (HAIGL) combines computer hardware expertise with AI governance. Both teams focus heavily on AI verification. Both are growing fast, with many ways to get involved. Ben Harack leads the verification initiatives and welcomes contact from anyone who wants to contribute.",
    links: [
      { text: "aigi.ox.ac.uk", url: "https://aigi.ox.ac.uk/" },
      { text: "Hardware AI Governance Lab", url: "https://aigi.ox.ac.uk/hardware-ai-governance-lab/" },
      { text: "Contact: Ben Harack", url: "mailto:benjamin.harack@politics.ox.ac.uk" },
    ],
  },
  {
    org: "ARIA: Trust Everything, Everywhere",
    tagline: "Opportunity seeds, up to £500K per proposal",
    tags: ["funding"],
    blurb:
      "ARIA (the UK's Advanced Research and Invention Agency) is funding opportunity seeds under its Trust Everything, Everywhere opportunity space, which aims to extend digital trust infrastructure into the physical world. Relevant topic areas include hardware and silicon security, cryptography, and trust tools that make physical security systems easier to design and verify. Seeds are up to £500K per proposal, with applications open until July 27.",
    links: [
      {
        text: "Opportunity seeds at ARIA",
        url: "https://aria.org.uk/opportunity-spaces/trust-everything-everywhere/opportunity-seeds",
      },
    ],
  },
  {
    org: "Frontier AI Security Residency",
    tagline: "8 weeks, fully funded, Cambridge UK",
    tags: ["jobs", "learning"],
    blurb:
      "An 8-week, fully funded research and engineering programme in Cambridge, UK, where residents take on concrete projects in cybersecurity and hardware-enabled verification to mitigate the risks of frontier AI. With the possibility of extension funding for up to 6 months, it is an easy way to transition into AI verification by working with leading organizations and academics in the field. Applications are open until July 26.",
    links: [{ text: "securefrontier.ai", url: "https://www.securefrontier.ai/" }],
  },
  {
    org: "Verifiable Governance Community Night",
    tagline: "The verification crowd, in person (San Francisco)",
    tags: ["community"],
    blurb:
      "A recurring happy hour for the AI verification scene, rotating between host organizations: technical folks, policy folks, and anyone curious about how to make AI systems whose properties can actually be checked. Pizza, drinks, and the people you should be talking to. Event spaces and co-hosts are welcome.",
    links: [
      { text: "RSVP to the next event", url: "https://partiful.com/e/pcCBC6h2ixRnqMHk09yM" },
    ],
  },
];

const externalLinkProps = (url: string) =>
  url.startsWith("/") || url.startsWith("#") ? {} : { target: "_blank", rel: "noopener noreferrer" };

export default function OpportunitiesBoard() {
  const [activeTag, setActiveTag] = useState<OpportunityTag | null>(null);

  const tagsInUse = TAG_ORDER.filter((tag) =>
    OPPORTUNITY_POSTINGS.some((posting) => posting.tags.includes(tag)),
  );
  const visiblePostings = activeTag
    ? OPPORTUNITY_POSTINGS.filter((posting) => posting.tags.includes(activeTag))
    : OPPORTUNITY_POSTINGS;

  return (
    <div className="not-prose my-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveTag(null)}
          className={`rounded-md border-[1.5px] border-black px-2.5 py-[3px] font-mono text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors duration-150 ${
            activeTag === null ? "bg-black text-white" : "text-black hover:bg-black hover:text-white"
          }`}
        >
          All
        </button>
        {tagsInUse.map((tag) => (
          <button
            key={tag}
            onClick={() => setActiveTag((prev) => (prev === tag ? null : tag))}
            className={`rounded-md border-[1.5px] border-black px-2.5 py-[3px] font-mono text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors duration-150 ${
              activeTag === tag ? "bg-black text-white" : "text-black hover:bg-black hover:text-white"
            }`}
          >
            {TAG_LABELS[tag]}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {visiblePostings.map((posting) => (
          <div
            key={posting.org}
            className="flex flex-col rounded-md border-[1.5px] border-black bg-[#fffff8] p-5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-[1.15rem] font-bold leading-snug text-[#1a1a1a]">{posting.org}</span>
              <span className="flex shrink-0 gap-1.5">
                {posting.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-[3px] border border-black/50 px-1.5 py-[1px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-black/70"
                  >
                    {TAG_LABELS[tag]}
                  </span>
                ))}
              </span>
            </div>
            <div className="mt-0.5 text-[0.92rem] italic leading-snug text-[#1a1a1a]/70">{posting.tagline}</div>
            <p className="mb-0 mt-2.5 grow text-[0.95rem] leading-[1.55] text-[#1a1a1a]">{posting.blurb}</p>
            <div className="mt-3.5 flex flex-wrap gap-x-4 gap-y-1.5">
              {posting.links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  {...externalLinkProps(link.url)}
                  className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8b0000] no-underline hover:underline"
                >
                  {link.text} {link.url.startsWith("#") ? "↑" : "↗"}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
